import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook,
  faCheckCircle,
  faForwardStep,
  faChevronDown,
  faLink,
  faFilePowerpoint,
  faStar,
  faCircleExclamation,
  faPlay,
  faChalkboardUser,
} from '@fortawesome/free-solid-svg-icons';
import CourseKindTag, { kindOf, kindCardClass } from '../components/CourseKind/CourseKind';
import ClassStats from '../components/CourseKind/ClassStats';
import './StudentClasses.css';

const API = process.env.REACT_APP_API_BASE_URL;

// Same fallback image as before, but with an absolute path (the old relative path broke on /StudentProfile/:id)
const DEFAULT_IMG = '/assets/coursesimages/206071006_a1d57977-9a9b-4428-9a06-4027494bf664-1024x576.jpg';

const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? `${d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })} · ${d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })}`
    : '';
};

// Same rule as before: a module's progress counts its current level (8 / 16 / 24 lessons)
const levelLimit = (count) => {
  if (count > 8 && count < 16) return 8;
  if (count > 16 && count < 24) return 16;
  if (count > 24) return 24;
  return count;
};

const Stars = ({ rating }) => {
  const r = Math.round(Number(rating) || 0);
  return (
    <span className="rmtsc-stars" aria-label={`Rated ${r} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <FontAwesomeIcon key={n} icon={faStar} className={n <= r ? 'is-on' : ''} />
      ))}
    </span>
  );
};

const StudentClasses = ({ StudentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [open, setOpen] = useState({}); // moduleName -> bool

  // ---------- Data (same endpoint) ----------
  useEffect(() => {
    if (!StudentId) {
      setError('No student selected.');
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/student/studentclasses/${StudentId}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error('Error fetching student classes:', err);
        if (!cancelled) setError('We couldn’t load this student’s classes.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [StudentId, reload]);

  // ---------- Group lessons by module, with progress (same rules) ----------
  const modules = useMemo(() => {
    if (!data?.myclasses) return [];
    const done = new Map(); // IdClass -> completions[]
    (data.myallclassesmade || []).forEach((c) => {
      if (!done.has(c.IdClass)) done.set(c.IdClass, []);
      done.get(c.IdClass).push(c);
    });

    const groups = [];
    const byName = {};
    data.myclasses.forEach((lesson) => {
      if (!byName[lesson.ModuleName]) {
        byName[lesson.ModuleName] = { name: lesson.ModuleName, lessons: [] };
        groups.push(byName[lesson.ModuleName]);
      }
      byName[lesson.ModuleName].lessons.push({ ...lesson, completions: done.get(lesson.Id) || [] });
    });

    return groups.map((g) => {
      const total = levelLimit(g.lessons.length);
      // any project of the module counts (a skipped project 8 replaced by project 9 is still 8/8),
      // never more than the module's level (8 / 16 / 24)
      const completed = Math.min(total, g.lessons.filter((l) => l.completions.length > 0).length);
      const percent = total ? Math.round((completed / total) * 100) : 0;
      const nextIndex = g.lessons.findIndex((l) => l.completions.length === 0);
      const state = total && completed === total ? 'done' : completed > 0 ? 'progress' : 'new';
      return { ...g, total, completed, percent, nextIndex, state };
    });
  }, [data]);

  // Open the module the student is working on (first in progress, else first not finished)
  useEffect(() => {
    if (!modules.length) return;
    const current = modules.find((m) => m.state === 'progress') || modules.find((m) => m.state !== 'done') || modules[0];
    setOpen({ [current.name]: true });
  }, [modules]);

  const toggle = (name) => setOpen((prev) => ({ ...prev, [name]: !prev[name] }));

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="rmtsc" aria-busy="true">
        <div className="rmtsc-stats">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rmtsc-skel rmtsc-skel--stat" />
          ))}
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} className="rmtsc-skel rmtsc-skel--row" />
        ))}
      </div>
    );
  }

  // ---------- Error ----------
  if (error) {
    return (
      <div className="rmtsc">
        <div className="rmtsc-state">
          <FontAwesomeIcon icon={faCircleExclamation} className="rmtsc-state-icon is-error" />
          <h2 className="rmtsc-state-title">Something went wrong</h2>
          <p>{error}</p>
          <button type="button" className="rmtsc-btn" onClick={() => setReload((n) => n + 1)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  // ---------- Empty ----------
  if (!modules.length) {
    return (
      <div className="rmtsc">
        <div className="rmtsc-state">
          <FontAwesomeIcon icon={faBook} className="rmtsc-state-icon" />
          <h2 className="rmtsc-state-title">No lessons yet</h2>
          <p>This student’s course lessons will appear here once their plan is set.</p>
        </div>
      </div>
    );
  }

  // overall = the modules' levels (8 / 16 / 24), not every extra project of a module
  const overallTotal = modules.reduce((n, m) => n + m.total, 0);
  const overallDone = modules.reduce((n, m) => n + m.completed, 0);
  const percent = overallTotal
    ? Math.round((overallDone / overallTotal) * 100)
    : Math.max(0, Math.min(100, Number(data.percent) || 0));

  return (
    <div className="rmtsc">
      {/* ---------- Stats ---------- */}
      <ClassStats
        percent={percent}
        lessonsDone={overallTotal ? overallDone : data.nbofprojectsdone || 0}
        lessonsTotal={overallTotal || data.totalprojects || 0}
        classesTaken={data.nbofclasstaken || 0}
        classesTotal={data.nbofclass || 0}
        sessionTypes={data.sessionTypes}
        hidePaid
      />

      <h2 className="rmtsc-section">Course modules</h2>

      {/* ---------- Modules ---------- */}
      <div className="rmtsc-modules">
        {modules.map((m) => {
          const isOpen = Boolean(open[m.name]);
          const panelId = `rmtsc-panel-${m.name.replace(/[^a-z0-9]+/gi, '-')}`;
          return (
            <section key={m.name} className={`rmtsc-module is-${m.state} ${isOpen ? 'is-open' : ''} ${kindCardClass(kindOf(m.lessons))}`}>
              <CourseKindTag kind={kindOf(m.lessons)} />
              <h3 className="rmtsc-module-h">
                <button
                  type="button"
                  className="rmtsc-module-head"
                  onClick={() => toggle(m.name)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <span className={`rmtsc-badge is-${m.state}`}>
                    {m.state === 'done' ? 'Completed' : m.state === 'progress' ? 'In progress' : 'Not started'}
                  </span>
                  <span className="rmtsc-module-name">{m.name}</span>
                  <span className="rmtsc-module-progress">
                    <span className="rmtsc-bar" aria-hidden="true">
                      <span style={{ width: `${m.percent}%` }} />
                    </span>
                    <span className="rmtsc-module-count">
                      {m.completed}/{m.total}
                    </span>
                  </span>
                  <FontAwesomeIcon icon={faChevronDown} className="rmtsc-chev" />
                </button>
              </h3>

              {isOpen && (
                <ol id={panelId} className="rmtsc-lessons">
                  {m.lessons.map((l, i) => {
                    const isDone = l.completions.length > 0;
                    // skipped = marked "Skipped class" (not done in a class): counts for the module, shown in grey
                    const isSkipped = isDone && l.completions.every((c) => Number(c.Skipped) === 1);
                    const isNext = i === m.nextIndex;
                    return (
                      <li key={`${l.Id}-${i}`} className={`rmtsc-lesson ${isDone ? 'is-done' : ''} ${isSkipped ? 'is-skipped' : ''} ${isNext ? 'is-next' : ''}`}>
                        <div className="rmtsc-lesson-row">
                          <span className="rmtsc-num" aria-hidden="true">
                            {isSkipped ? <FontAwesomeIcon icon={faForwardStep} /> : isDone ? <FontAwesomeIcon icon={faCheckCircle} /> : i + 1}
                          </span>

                          <img
                            className="rmtsc-thumb"
                            src={l.CourseFilesLink || DEFAULT_IMG}
                            alt=""
                            loading="lazy"
                            onError={(e) => {
                              if (!e.currentTarget.src.endsWith(DEFAULT_IMG)) e.currentTarget.src = DEFAULT_IMG;
                            }}
                          />

                          <div className="rmtsc-lesson-body">
                            <span className="rmtsc-session">Session {i + 1}</span>
                            <h4>
                              {(l.CourseName || '').trim()}
                              {isNext && (
                                <span className="rmtsc-next-tag">
                                  <FontAwesomeIcon icon={faPlay} /> Next to teach
                                </span>
                              )}
                            </h4>
                          </div>

                          {/* teachers need these for every lesson, done or not (same as before) */}
                          {(l.CourseLink || l.CourseProjectLink) && (
                            <div className="rmtsc-actions">
                              {l.CourseLink && (
                                <a href={l.CourseLink} target="_blank" rel="noopener noreferrer">
                                  <FontAwesomeIcon icon={faLink} /> Open project
                                </a>
                              )}
                              {l.CourseProjectLink && (
                                <a href={l.CourseProjectLink} target="_blank" rel="noopener noreferrer">
                                  <FontAwesomeIcon icon={faFilePowerpoint} /> View PPT
                                </a>
                              )}
                            </div>
                          )}
                        </div>

                        {isDone && (
                          <ul className="rmtsc-done">
                            {l.completions.map((c, idx) => (
                              <li key={`${l.Id}-c-${idx}`}>
                                <span className="rmtsc-done-who">
                                  <FontAwesomeIcon icon={faChalkboardUser} />
                                  {Number(c.Skipped) === 1 ? 'Skipped (not done in a class)' : [c.FirstName, c.LastName].filter(Boolean).join(' ') || 'Teacher'}
                                </span>
                                <span className="rmtsc-done-when">{fmtDate(c.ClassDateTime)}</span>
                                {Number(c.Skipped) !== 1 && <Stars rating={c.Rating} />}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default StudentClasses;